// src/modules/dl/RegularizationLab.jsx
// Regularization Lab — VISUAL network simulator
// See dropout masks on neurons, weight distributions, BN normalizing activations live

import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--orange)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,transition:'all .12s',...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',borderRadius:'2px',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

const TABS = ['overfit','dropout','l1-l2','batchnorm','early-stop','compare'];

// ─── MATH ────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

// Simulate train/val curves
const simCurves = ({epochs=200,overfitRate=0.8,noiseScale=0.02,valFloor=0.3,trainFloor=0.05,explode=false}) => {
  const pts=[]; let tr=1.0,va=1.0;
  for(let e=0;e<epochs;e++){
    tr=Math.max(trainFloor,tr*(1-(0.005+0.003*overfitRate)))+randn()*noiseScale;
    const div=e>epochs*0.4?overfitRate*0.002*(e-epochs*0.4):0;
    va=Math.max(valFloor-0.15*(1-overfitRate),va*(1-0.003*(1.1-overfitRate*0.7))+div)+randn()*noiseScale;
    if(explode&&e>100) va=Math.min(va+(e-100)*0.005,2.5);
    if(e%2===0) pts.push({epoch:e,train:+Math.max(0,tr).toFixed(4),val:+Math.max(0,va).toFixed(4)});
  }
  return pts;
};

// ─── VISUAL LAYER ROW (neurons as circles with color) ────────────────────────
function LayerRow({ n, acts, dropped, label, color, R=14 }) {
  const W = Math.min(700, n*((R*2)+4)+40);
  const H = R*2+20;
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{label} ({n} neurons)</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:'block',maxHeight:60}}>
        {Array.from({length:n},(_,i)=>{
          const x = 20+i*((R*2)+4)+R;
          const y = H/2;
          const a = acts?.[i]??0;
          const isDead = dropped?.[i] === true;
          const fill = isDead ? '#1e293b' : `rgba(${color==='cyan'?'34,211,238':color==='green'?'74,222,128':color==='violet'?'167,139,250':color==='gold'?'251,191,36':'251,146,60'},${0.1+Math.abs(a)*0.9})`;
          const stroke = isDead ? '#f87171' : (color==='cyan'?'#22d3ee':color==='green'?'#4ade80':color==='violet'?'#a78bfa':color==='gold'?'#fbbf24':'#fb923c');
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={R} fill={fill} stroke={isDead?'#f87171':stroke} strokeWidth={isDead?2:1} opacity={isDead?0.5:1}/>
              {isDead && <>
                <line x1={x-R*0.5} y1={y-R*0.5} x2={x+R*0.5} y2={y+R*0.5} stroke="#f87171" strokeWidth={1.5}/>
                <line x1={x+R*0.5} y1={y-R*0.5} x2={x-R*0.5} y2={y+R*0.5} stroke="#f87171" strokeWidth={1.5}/>
              </>}
              {R>=10&&!isDead&&<text x={x} y={y+0.5} textAnchor="middle" dominantBaseline="middle" style={{fontSize:`${R*0.55}px`,fill:'#f1f5f9',fontFamily:'JetBrains Mono',fontWeight:600,pointerEvents:'none'}}>{a.toFixed(1)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── NETWORK WITH DROPOUT VISUALIZATION ──────────────────────────────────────
function DropoutNet({ arch, acts, dropped, R=14 }) {
  const colors = ['cyan','violet','violet','green'];
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'12px',padding:'16px',background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b'}}>
      {arch.map((n,l)=>(
        <LayerRow key={l} n={n} acts={acts?.[l]} dropped={dropped?.[l]}
          label={l===0?'INPUT':l===arch.length-1?'OUTPUT':`HIDDEN ${l}`}
          color={colors[l]??'violet'} R={Math.min(R,Math.floor(320/n)-4)}/>
      ))}
    </div>
  );
}

// ─── WEIGHT DISTRIBUTION CHART ───────────────────────────────────────────────
function WeightDistChart({ before, after, label }) {
  if(!before?.length) return null;
  const bins=16, mn=Math.min(...before,...(after||[])), mx=Math.max(...before,...(after||[])), rng=mx-mn||0.001;
  const hBefore=Array(bins).fill(0), hAfter=Array(bins).fill(0);
  before.forEach(v=>{const b=Math.min(bins-1,Math.floor((v-mn)/rng*bins));hBefore[b]++;});
  (after||[]).forEach(v=>{const b=Math.min(bins-1,Math.floor((v-mn)/rng*bins));hAfter[b]++;});
  const data=hBefore.map((c,i)=>({x:(mn+i*rng/bins).toFixed(2),before:c,after:hAfter[i]}));
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>{label}</div>
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={data} margin={{top:2,bottom:0,left:0,right:0}}>
          <XAxis dataKey="x" tick={{fontSize:7,fill:'#475569'}} interval={3}/>
          <YAxis tick={{fontSize:7,fill:'#475569'}} width={18}/>
          <Bar dataKey="before" fill="#f87171" opacity={0.6} name="Before" radius={[1,1,0,0]}/>
          <Bar dataKey="after" fill="#4ade80" opacity={0.8} name="After" radius={[1,1,0,0]}/>
          <Tooltip contentStyle={{background:'#0f172a',border:'1px solid #1e293b',fontSize:'10px'}}/>
          <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── BATCHNORM ACTIVATION STRIP ──────────────────────────────────────────────
function BNStrip({ vals, label, color='#22d3ee', showScale=true }) {
  if(!vals?.length) return null;
  const mx=Math.max(0.01,...vals.map(Math.abs));
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{label}</div>
      <div style={{display:'flex',gap:'2px',flexWrap:'wrap'}}>
        {vals.slice(0,32).map((v,i)=>{
          const h=Math.max(4,Math.abs(v)/mx*40);
          const bg=`rgba(${color==='#22d3ee'?'34,211,238':color==='#4ade80'?'74,222,128':'167,139,250'},${0.1+Math.abs(v)/mx*0.9})`;
          return <div key={i} title={v.toFixed(3)}
            style={{width:12,height:h,background:bg,borderRadius:'1px',alignSelf:'flex-end',flexShrink:0,border:`1px solid ${color}44`}}/>;
        })}
      </div>
      {showScale&&<div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>
        μ={(vals.reduce((s,v)=>s+v,0)/vals.length).toFixed(2)}  σ={Math.sqrt(vals.reduce((s,v,_,a)=>s+(v-a.reduce((s,v)=>s+v,0)/a.length)**2,0)/vals.length).toFixed(2)}
      </div>}
    </div>
  );
}

export default function RegularizationLab() {
  const [tab,setTab]=useState('overfit');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Regularization Lab — overfit first, then fix it visually.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);
  const [running,setRunning]=useState(false);
  const run=async(fn)=>{setRunning(true);await new Promise(r=>setTimeout(r,20));fn();setRunning(false);};

  // ── OVERFIT ────────────────────────────────────────────────────
  const [ofCap,setOfCap]=useState(5);
  const [ofNoise,setOfNoise]=useState(0.1);
  const [ofResult,setOfResult]=useState(null);

  // ── DROPOUT ────────────────────────────────────────────────────
  const [dpRate,setDpRate]=useState(0.5);
  const [dpNeurons,setDpNeurons]=useState(12);
  const [dpResult,setDpResult]=useState(null);
  const [dpNet,setDpNet]=useState(null);   // {arch, acts, dropped}
  const [dpMode,setDpMode]=useState('training'); // training | inference

  // ── L1/L2 ──────────────────────────────────────────────────────
  const [l2,setL2]=useState(0.01);
  const [l1,setL1]=useState(0.0);
  const [regResult,setRegResult]=useState(null);

  // ── BATCHNORM ──────────────────────────────────────────────────
  const [bnGamma,setBnGamma]=useState(1.0);
  const [bnBeta,setBnBeta]=useState(0.0);
  const [bnMomentum,setBnMomentum]=useState(0.1);
  const [bnResult,setBnResult]=useState(null);

  // ── EARLY STOP ─────────────────────────────────────────────────
  const [esPat,setEsPat]=useState(15);
  const [esResult,setEsResult]=useState(null);

  // ── COMPARE ────────────────────────────────────────────────────
  const [cmpResult,setCmpResult]=useState(null);

  // ── OVERFIT RUN ───────────────────────────────────────────────
  const runOverfit=()=>run(()=>{
    const curves=simCurves({epochs:200,overfitRate:ofCap/5,noiseScale:ofNoise,valFloor:0.25+ofNoise,trainFloor:0.01,explode:ofCap>4});
    const fTr=curves.at(-1)?.train, fVa=curves.at(-1)?.val;
    const gap=+(fVa-fTr).toFixed(4);
    setOfResult({curves,gap,fTr,fVa,isOverfit:gap>0.15});
    log(`cap=${ofCap}x noise=${ofNoise} → train=${fTr} val=${fVa} gap=${gap} ${gap>0.15?'⚠ OVERFIT':'✓ OK'}`,'ok');
  });

  // ── DROPOUT RUN ───────────────────────────────────────────────
  const runDropout=()=>run(()=>{
    const arch=[4,dpNeurons,dpNeurons,1];
    // Simulate activations with dropout
    const acts=arch.map(n=>Array.from({length:n},()=>+Math.max(0,randn()*0.8+0.5).toFixed(2)));
    const dropped=arch.map((n,l)=>l===0||l===arch.length-1
      ? Array(n).fill(false)
      : Array.from({length:n},()=>Math.random()<dpRate)
    );
    // Scale surviving activations
    acts.forEach((layer,l)=>{
      if(l===0||l===arch.length-1) return;
      const scale = dpMode==='training' ? 1/(1-dpRate) : 1;
      layer.forEach((v,i)=>{acts[l][i]=dropped[l][i]?0:+(v*scale).toFixed(2);});
    });
    setDpNet({arch,acts,dropped});

    // Training curves comparison
    const withoutDp=simCurves({epochs:200,overfitRate:0.85,noiseScale:0.015,valFloor:0.28});
    const withDp=simCurves({epochs:200,overfitRate:0.85*(1-dpRate*0.7),noiseScale:0.02,valFloor:0.2+dpRate*0.08});
    const merged=withoutDp.map((p,i)=>({epoch:p.epoch,noDrop_val:p.val,withDrop_val:withDp[i]?.val,noDrop_train:p.train,withDrop_train:withDp[i]?.train}));
    const totalDead=dropped.flat().filter(Boolean).length;
    setDpResult({merged,totalDead,total:dropped.flat().length,
      gapBefore:+(withoutDp.at(-1)?.val-withoutDp.at(-1)?.train).toFixed(4),
      gapAfter:+(withDp.at(-1)?.val-withDp.at(-1)?.train).toFixed(4)});
    log(`Dropout p=${dpRate} mode=${dpMode} | ${totalDead}/${dropped.flat().length} dropped | gap: before=${+(withoutDp.at(-1)?.val-withoutDp.at(-1)?.train).toFixed(4)} after=${+(withDp.at(-1)?.val-withDp.at(-1)?.train).toFixed(4)}`,'ok');
  });

  // ── L1/L2 RUN ────────────────────────────────────────────────
  const runReg=()=>run(()=>{
    const n=50;
    const before=Array.from({length:n},()=>randn()*1.5);
    const afterL2=before.map(w=>w*(1-l2*2));
    const afterL1=before.map(w=>Math.max(0,Math.abs(w)-l1*0.5)*Math.sign(w));
    const afterBoth=before.map(w=>{let v=w*(1-l2*2);return Math.max(0,Math.abs(v)-l1*0.3)*Math.sign(v);});
    const spL1=afterL1.filter(v=>Math.abs(v)<0.05).length;
    const spBoth=afterBoth.filter(v=>Math.abs(v)<0.05).length;

    const noReg=simCurves({epochs:200,overfitRate:0.9,noiseScale:0.015,valFloor:0.3});
    const withReg=simCurves({epochs:200,overfitRate:0.9*(1-(l2+l1)*10),noiseScale:0.02,valFloor:0.22+(l2+l1)*3});
    const merged=noReg.map((p,i)=>({epoch:p.epoch,noReg_val:p.val,withReg_val:withReg[i]?.val}));

    setRegResult({before,afterL2,afterL1,afterBoth,spL1,spBoth,n,merged,
      gapBefore:+(noReg.at(-1)?.val-noReg.at(-1)?.train).toFixed(4),
      gapAfter:+(withReg.at(-1)?.val-withReg.at(-1)?.train).toFixed(4)});
    log(`L2=${l2} L1=${l1} | L1 sparse=${spL1}/${n} | Both sparse=${spBoth}/${n}`,'ok');
  });

  // ── BATCHNORM RUN ────────────────────────────────────────────
  const runBN=()=>run(()=>{
    const n=32;
    const raw=Array.from({length:n},()=>randn()*3+4); // biased: mean~4, std~3
    const mu=raw.reduce((s,v)=>s+v,0)/n;
    const sig=Math.sqrt(raw.reduce((s,v)=>s+(v-mu)**2,0)/n)||1;
    const normed=raw.map(v=>(v-mu)/sig);
    const scaled=normed.map(v=>bnGamma*v+bnBeta);
    // Running stats update
    const runMu=+(0.9*0+bnMomentum*mu).toFixed(3);
    const runSig=+(0.9*1+bnMomentum*sig).toFixed(3);

    const noBN=simCurves({epochs:200,overfitRate:0.7,noiseScale:0.025,valFloor:0.35});
    const withBN=simCurves({epochs:200,overfitRate:0.4,noiseScale:0.01,valFloor:0.15});
    const merged=noBN.map((p,i)=>({epoch:p.epoch,noBN_val:p.val,withBN_val:withBN[i]?.val}));

    setBnResult({raw,normed,scaled,mu:+mu.toFixed(3),sig:+sig.toFixed(3),runMu,runSig,merged});
    log(`γ=${bnGamma} β=${bnBeta} | raw: μ=${mu.toFixed(2)} σ=${sig.toFixed(2)} → scaled: μ≈${bnBeta} σ≈${bnGamma}`,'ok');
  });

  // ── EARLY STOP RUN ────────────────────────────────────────────
  const runES=()=>run(()=>{
    const curves=simCurves({epochs:200,overfitRate:0.75,noiseScale:0.02,valFloor:0.22});
    let bestEp=0,bestVal=curves[0]?.val;
    for(let i=0;i<curves.length;i++){if(curves[i].val<bestVal){bestVal=curves[i].val;bestEp=i;}}
    const stopEp=Math.min(curves.length-1,bestEp+Math.ceil(esPat/2));
    const saved=+((1-stopEp/curves.length)*100).toFixed(1);
    setEsResult({curves,bestEp,bestVal:+bestVal.toFixed(4),stopEp,saved});
    log(`patience=${esPat} | best=${bestVal.toFixed(4)}@ep${bestEp} | stop@${stopEp} | saved=${saved}%`,'ok');
  });

  // ── COMPARE RUN ───────────────────────────────────────────────
  const runCompare=()=>run(()=>{
    const cfgs=[
      {name:'No Reg',color:P[5],overfitRate:0.9,noiseScale:0.015,valFloor:0.32},
      {name:'Dropout 0.3',color:P[1],overfitRate:0.6,noiseScale:0.02,valFloor:0.22},
      {name:'L2 λ=0.01',color:P[2],overfitRate:0.55,noiseScale:0.018,valFloor:0.20},
      {name:'L1 λ=0.01',color:P[3],overfitRate:0.5,noiseScale:0.02,valFloor:0.19},
      {name:'BatchNorm',color:P[4],overfitRate:0.4,noiseScale:0.01,valFloor:0.16},
      {name:'All Combined',color:P[0],overfitRate:0.25,noiseScale:0.01,valFloor:0.13},
    ];
    const results=cfgs.map(cfg=>{
      const c=simCurves({epochs:200,...cfg});
      return{...cfg,curves:c,fTr:c.at(-1)?.train,fVa:c.at(-1)?.val,gap:+(c.at(-1)?.val-c.at(-1)?.train).toFixed(4)};
    });
    const chart=results[0].curves.map((p,i)=>{
      const pt={epoch:p.epoch};
      results.forEach(r=>{pt[r.name]=r.curves[i]?.val;});
      return pt;
    });
    setCmpResult({results,chart});
    const win=results.reduce((b,r)=>r.fVa<b.fVa?r:b,results[0]);
    log(`Compare done | winner: ${win.name} val=${win.fVa}`,'ok');
  });

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── OVERFIT ── */}
      {tab==='overfit'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="DIAL IN OVERFIT" accent={AC}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.5}}>Crank capacity + noise + small data = big gap.</div>
              <Sl label="Network Capacity" value={ofCap} min={1} max={5} step={1} onChange={setOfCap} color={AC} unit="x"/>
              <Sl label="Data Noise" value={ofNoise} min={0.01} max={0.5} step={0.01} onChange={setOfNoise} color="#f87171"/>
              <Btn onClick={runOverfit} disabled={running} color={AC} style={{width:'100%',marginTop:'4px'}}>{running?'…':'OVERFIT IT →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={130}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {ofResult
              ? <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                    <Metric label="Train Loss" value={ofResult.fTr} color="var(--cyan)"/>
                    <Metric label="Val Loss" value={ofResult.fVa} color={ofResult.isOverfit?'var(--red)':'var(--green)'}/>
                    <Metric label="Gap" value={ofResult.gap} color={ofResult.isOverfit?'var(--red)':'var(--green)'}/>
                    <Metric label="Status" value={ofResult.isOverfit?'OVERFIT':'GOOD'} color={ofResult.isOverfit?'var(--red)':'var(--green)'}/>
                  </div>
                  <Panel title="TRAIN vs VALIDATION" accent={ofResult.isOverfit?'var(--red)':AC}>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={ofResult.curves}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        <Line type="monotone" dataKey="train" stroke="#22d3ee" strokeWidth={2} dot={false}/>
                        <Line type="monotone" dataKey="val" stroke="#f87171" strokeWidth={2} dot={false} strokeDasharray="5 3"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Crank capacity to 5x, noise high → overfit it</div>
            }
          </div>
        </div>
      )}

      {/* ── DROPOUT ── */}
      {tab==='dropout'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="DROPOUT CONTROLS" accent="var(--cyan)">
              <Sl label="Dropout Rate p" value={dpRate} min={0} max={0.9} step={0.05} onChange={setDpRate} color="#22d3ee"/>
              <Sl label="Layer Width" value={dpNeurons} min={6} max={20} step={2} onChange={setDpNeurons} color="#a78bfa"/>
              <div style={{display:'flex',gap:'6px',marginBottom:'8px'}}>
                {['training','inference'].map(m=>(
                  <button key={m} onClick={()=>setDpMode(m)} style={{flex:1,padding:'5px',border:`1px solid ${dpMode===m?'#22d3ee':'var(--b0)'}`,background:dpMode===m?'#22d3ee18':'transparent',color:dpMode===m?'#22d3ee':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{m}</button>
                ))}
              </div>
              <Btn onClick={runDropout} disabled={running} color="#22d3ee" style={{width:'100%'}}>{running?'…':'APPLY DROPOUT →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>Training: random × dropped. Inference: all neurons active.</div>
            </Panel>
            {dpResult&&<Panel title="STATS" accent="var(--cyan)">
              {[['Dropped',`${dpResult.totalDead}/${dpResult.total}`,'#f87171'],['Gap before',dpResult.gapBefore,'#f87171'],['Gap after',dpResult.gapAfter,'#4ade80'],['Scale',`÷${(1-dpRate).toFixed(2)}`,'#22d3ee']].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px'}}>
                  <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                </div>
              ))}
            </Panel>}
            <LogPanel logs={logs} height={80}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {dpNet&&(
              <Panel title={`NETWORK WITH DROPOUT (${dpMode.toUpperCase()}) — RED × = DROPPED NEURON`} accent="var(--cyan)">
                <DropoutNet arch={dpNet.arch} acts={dpNet.acts} dropped={dpNet.dropped} R={16}/>
                <div style={{display:'flex',gap:'12px',marginTop:'8px',fontSize:'9px',color:'var(--t3)'}}>
                  <span><span style={{color:'#22d3ee'}}>●</span> active (scaled ×{(1/(1-dpRate)).toFixed(2)})</span>
                  <span><span style={{color:'#f87171'}}>✕</span> dropped (zero)</span>
                </div>
              </Panel>
            )}
            {dpResult&&(
              <Panel title="WITH vs WITHOUT DROPOUT — VAL LOSS" accent="var(--cyan)">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={dpResult.merged}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                    <Tooltip/>
                    <Legend/>
                    <Line type="monotone" dataKey="noDrop_val" stroke="#f87171" strokeWidth={2} dot={false} name="No Dropout (val)"/>
                    <Line type="monotone" dataKey="withDrop_val" stroke="#22d3ee" strokeWidth={2} dot={false} name="Dropout (val)"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            )}
            {!dpNet&&<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Slide rate, hit APPLY — see neurons drop</div>}
          </div>
        </div>
      )}

      {/* ── L1/L2 ── */}
      {tab==='l1-l2'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="WEIGHT PENALTY" accent="var(--green)">
              <Sl label="L2 λ (ridge — shrinks all)" value={l2} min={0} max={0.1} step={0.001} onChange={setL2} color="#4ade80"/>
              <Sl label="L1 λ (lasso — kills weak)" value={l1} min={0} max={0.1} step={0.001} onChange={setL1} color="#fbbf24"/>
              <Btn onClick={runReg} disabled={running} color="#4ade80" style={{width:'100%',marginTop:'4px'}}>{running?'…':'APPLY →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>L1=0.05 → many weights collapse to 0 (sparsity). Try it.</div>
            </Panel>
            {regResult&&<Panel title="SPARSITY" accent="var(--gold)">
              {[['Before',`0/${regResult.n}`,'var(--t3)'],['After L2',`0/${regResult.n}`,'#4ade80'],['After L1',`${regResult.spL1}/${regResult.n}`,'#fbbf24'],['Gap →',`${regResult.gapBefore}→${regResult.gapAfter}`,'#22d3ee']].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px'}}>
                  <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                </div>
              ))}
            </Panel>}
            <LogPanel logs={logs} height={80}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {regResult
              ? <>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                    <Panel title="WEIGHT DISTRIBUTION — BEFORE vs AFTER" accent="var(--green)">
                      <WeightDistChart before={regResult.before} after={regResult.afterL2} label="L2 Effect (blue = after)"/>
                      <div style={{marginTop:'8px'}}/>
                      <WeightDistChart before={regResult.before} after={regResult.afterL1} label="L1 Effect (blue = after)"/>
                    </Panel>
                    <Panel title="VAL LOSS — REG vs NO REG" accent="var(--green)">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={regResult.merged}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                          <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <Tooltip/>
                          <Legend/>
                          <Line type="monotone" dataKey="noReg_val" stroke="#f87171" strokeWidth={2} dot={false} name="No Reg (val)"/>
                          <Line type="monotone" dataKey="withReg_val" stroke="#4ade80" strokeWidth={2} dot={false} name="With Reg (val)"/>
                        </LineChart>
                      </ResponsiveContainer>
                    </Panel>
                  </div>
                  {/* Visual weight bars */}
                  <Panel title="WEIGHT VALUES — EACH BAR = ONE WEIGHT (hover for value)" accent="var(--gold)">
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'12px'}}>
                      {[['Before',regResult.before,'#f87171'],['After L2',regResult.afterL2,'#4ade80'],['After L1',regResult.afterL1,'#fbbf24'],['L1+L2',regResult.afterBoth,'#a78bfa']].map(([title,vals,color])=>{
                        const mx=Math.max(0.01,...vals.map(Math.abs));
                        return(
                          <div key={title}>
                            <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>{title}</div>
                            <div style={{display:'flex',gap:'2px',alignItems:'flex-end',height:60}}>
                              {vals.map((v,i)=>{
                                const h=Math.max(1,Math.abs(v)/mx*56);
                                return <div key={i} title={v.toFixed(3)}
                                  style={{flex:1,height:h,background:Math.abs(v)<0.05?'#334155':color,borderRadius:'1px 1px 0 0',opacity:0.85,minWidth:3}}/>;
                              })}
                            </div>
                            <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>
                              {vals.filter(v=>Math.abs(v)<0.05).length} near-zero
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Set L1=0.05 to see weights collapse</div>
            }
          </div>
        </div>
      )}

      {/* ── BATCHNORM ── */}
      {tab==='batchnorm'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="BN PARAMS" accent="var(--violet)">
              <Sl label="γ (scale)" value={bnGamma} min={0.1} max={3} step={0.1} onChange={setBnGamma} color="#a78bfa"/>
              <Sl label="β (shift)" value={bnBeta} min={-2} max={2} step={0.1} onChange={setBnBeta} color="#fbbf24"/>
              <Sl label="Momentum" value={bnMomentum} min={0.01} max={0.5} step={0.01} onChange={setBnMomentum} color="#22d3ee"/>
              <Btn onClick={runBN} disabled={running} color="#a78bfa" style={{width:'100%',marginTop:'4px'}}>{running?'…':'APPLY BATCHNORM →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>γ=1 β=0 = pure normalize. Slide γ to rescale. β to shift output distribution.</div>
            </Panel>
            {bnResult&&<Panel title="STATS" accent="var(--violet)">
              {[['Raw μ',bnResult.mu,'#f87171'],['Raw σ',bnResult.sig,'#f87171'],['Norm μ','≈ 0','#4ade80'],['Norm σ','≈ 1','#4ade80'],['Out μ',bnBeta.toFixed(1),'#a78bfa'],['Out σ',bnGamma.toFixed(1),'#a78bfa'],['Run μ',bnResult.runMu,'#22d3ee'],['Run σ',bnResult.runSig,'#22d3ee']].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',fontSize:'10px'}}>
                  <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                </div>
              ))}
            </Panel>}
            <LogPanel logs={logs} height={60}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {bnResult
              ? <>
                  <Panel title="ACTIVATIONS: RAW → NORMALIZED → SCALED (γ,β)" accent="var(--violet)">
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',padding:'12px',background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b'}}>
                      <BNStrip vals={bnResult.raw} label="RAW (biased)" color="#f87171"/>
                      <BNStrip vals={bnResult.normed} label="NORMALIZED (μ≈0 σ≈1)" color="#22d3ee"/>
                      <BNStrip vals={bnResult.scaled} label={`SCALED (γ=${bnGamma} β=${bnBeta})`} color="#a78bfa"/>
                    </div>
                  </Panel>
                  <Panel title="TRAINING: WITH vs WITHOUT BATCHNORM" accent="var(--violet)">
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={bnResult.merged}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        <Line type="monotone" dataKey="noBN_val" stroke="#f87171" strokeWidth={2} dot={false} name="No BN (val)"/>
                        <Line type="monotone" dataKey="withBN_val" stroke="#a78bfa" strokeWidth={2} dot={false} name="With BN (val)"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Hit APPLY BATCHNORM, slide γ and β</div>
            }
          </div>
        </div>
      )}

      {/* ── EARLY STOP ── */}
      {tab==='early-stop'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="PATIENCE" accent="var(--gold)">
              <Sl label="Patience (epochs)" value={esPat} min={5} max={50} step={5} onChange={setEsPat} color="#fbbf24"/>
              <Btn onClick={runES} disabled={running} color="#fbbf24" style={{width:'100%',marginTop:'4px'}}>{running?'…':'RUN →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>Low patience = stops too early. High = wastes compute.</div>
            </Panel>
            {esResult&&<Panel title="RESULT" accent="var(--gold)">
              {[['Best epoch',esResult.bestEp,'#4ade80'],['Best val',esResult.bestVal,'#4ade80'],['Stopped @',`ep ${esResult.stopEp}`,'#fbbf24'],['Saved',`${esResult.saved}%`,'#22d3ee']].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px'}}>
                  <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                </div>
              ))}
            </Panel>}
            <LogPanel logs={logs} height={80}/>
          </div>
          <div>
            {esResult
              ? <Panel title="LOSS CURVE WITH EARLY STOP MARKERS" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={esResult.curves}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="train" stroke="#22d3ee" strokeWidth={2} dot={false}/>
                      <Line type="monotone" dataKey="val" stroke="#f87171" strokeWidth={2} dot={false}/>
                      <ReferenceLine x={esResult.curves[esResult.bestEp]?.epoch} stroke="#4ade80" strokeWidth={2} label={{value:'BEST',fill:'#4ade80',fontSize:9}}/>
                      <ReferenceLine x={esResult.curves[esResult.stopEp]?.epoch} stroke="#fbbf24" strokeDasharray="5 3" strokeWidth={2} label={{value:'STOP',fill:'#fbbf24',fontSize:9}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Slide patience, run it</div>
            }
          </div>
        </div>
      )}

      {/* ── COMPARE ── */}
      {tab==='compare'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="HEAD-TO-HEAD" accent={AC}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.5}}>Same network · same data · 6 strategies. Who wins?</div>
              <Btn onClick={runCompare} disabled={running} color={AC} style={{width:'100%'}}>{running?'RUNNING…':'RUN ALL →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={180}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {cmpResult
              ? <>
                  <Panel title="VALIDATION LOSS — ALL STRATEGIES" accent={AC}>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={cmpResult.chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        {cmpResult.results.map(r=><Line key={r.name} type="monotone" dataKey={r.name} stroke={r.color} strokeWidth={r.name==='All Combined'?3:1.5} dot={false}/>)}
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="FINAL LEADERBOARD" accent={AC}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                      <thead><tr style={{background:'var(--deep)'}}>
                        {['Rank','Method','Train','Val','Gap','Verdict'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {[...cmpResult.results].sort((a,b)=>a.fVa-b.fVa).map((r,i)=>(
                          <tr key={r.name} style={{borderBottom:'1px solid var(--b0)',background:i%2?'rgba(0,0,0,.06)':'transparent'}}>
                            <td style={{padding:'7px 10px',color:P[i],fontFamily:'Syne',fontWeight:800,fontSize:'14px'}}>{i+1}</td>
                            <td style={{padding:'7px 10px',color:r.color,fontWeight:600}}>{r.name}</td>
                            <td style={{padding:'7px 10px',color:'#22d3ee',fontFamily:'JetBrains Mono'}}>{r.fTr}</td>
                            <td style={{padding:'7px 10px',color:r.gap>0.15?'#f87171':'#4ade80',fontFamily:'JetBrains Mono'}}>{r.fVa}</td>
                            <td style={{padding:'7px 10px',color:r.gap>0.15?'#f87171':'#4ade80',fontFamily:'JetBrains Mono'}}>{r.gap}</td>
                            <td style={{padding:'7px 10px',color:'var(--t3)',fontSize:'10px'}}>{r.gap<0.05?'✓ Great':r.gap<0.15?'~ OK':'✗ Overfit'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Hit RUN ALL — all 6 strategies on the same problem</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}
